import { api } from './api';

/**
 * Group holds and chargebacks (D-99, D-100).
 *
 * Both are thin: the work is on the server, and these are the calls the two
 * new pages make.
 */

export interface GroupHold {
  id: string;
  order_id: string;
  event_id: string;
  performance_id: string;
  organization_name: string;
  organization_id: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  seat_count: number;
  status: 'HELD' | 'PAID' | 'RELEASED' | 'CANCELLED';
  note: string | null;
  released_at: string | null;
  release_reason: string | null;
  created_at: string;
  updated_at: string;
  event_title: string;
  performance_starts_at: string;
  timezone: string;
  order_number: string;
  payment_status: string;
  payment_due_at: string | null;
  total_cents: number;
  balance_due_cents: number;
}

export interface GroupHoldInput {
  organization_name: string;
  organization_id?: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone?: string | null;
  /** The day to pay by; the group has until the end of it where the venue is (D-336). */
  due_on: string;
  grace_hours?: number;
  note?: string | null;
  send_link?: boolean;
  save_organization?: boolean;
}

export interface GroupHoldDetail {
  group_hold: Omit<GroupHold, 'event_title' | 'performance_starts_at' | 'timezone' | 'order_number' | 'payment_status' | 'payment_due_at' | 'total_cents' | 'balance_due_cents'>;
  order: { order_number: string; payment_status: string; payment_due_at: string | null; grace_hours: number | null; total_cents: number; paid_cents: number; balance_due_cents: number };
  performance: { event_title: string; starts_at: string; timezone: string; venue_name: string };
  seats: { order_item_id: string; status: string; section: string | null; table: string | null; row: string | null; seat: string | null; ga_area: string | null; ticket_type: string | null; price_cents: number }[];
  payment_requests: { id: string; status: string; due_at: string | null; sent_at: string | null; send_count: number; last_viewed_at: string | null; recipient_email: string }[];
  reminders: { offset_code: string; scheduled_for: string; sent_at: string | null }[];
}

export const groupSalesEndpoints = {
  list: (params: { status?: string; performance_id?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.performance_id) query.set('performance_id', params.performance_id);
    return api.get<{ group_holds: GroupHold[] }>(`/api/admin/group-holds${query.size ? `?${query}` : ''}`);
  },
  create: (input: GroupHoldInput) =>
    api.post<{ group_hold: GroupHold; order_id: string; total_cents: number; payment_request: { id: string; url: string } | null }>('/api/admin/group-holds', input),
  release: (id: string, reason: string) => api.post<{ group_hold: GroupHold }>(`/api/admin/group-holds/${id}/release`, { reason }),
  detail: (id: string) => api.get<GroupHoldDetail>(`/api/admin/group-holds/${id}`),
  deadline: (id: string, input: { due_on: string; grace_hours?: number | null }) => api.patch<{ group_hold: GroupHold }>(`/api/admin/group-holds/${id}/deadline`, input),
  resend: (id: string) => api.post<{ url: string; created: boolean }>(`/api/admin/group-holds/${id}/resend`),
  giveBack: (id: string, input: { order_item_ids: string[]; reason: string }) => api.post<{ group_hold: GroupHold }>(`/api/admin/group-holds/${id}/give-back`, input),
  /** The invoice PDF (D-351): opened in a tab, where the staff session's cookie goes with it. */
  invoiceUrl: (id: string) => `/api/admin/group-holds/${id}/invoice.pdf`,
};

// --- Chargebacks ---------------------------------------------------------------------------

export interface Dispute {
  id: string;
  stripe_dispute_id: string;
  order_id: string;
  amount_cents: number;
  reason: string | null;
  stripe_status: string;
  outcome: string;
  evidence_due_by: string | null;
  evidence_submitted_at: string | null;
  tickets_revoked: number;
  tickets_revoked_at: string | null;
  staff_note: string | null;
  stripe_created_at: string;
  closed_at: string | null;
  order_number: string;
  customer_name: string | null;
  customer_email: string | null;
  live_ticket_count: number;
  event_titles: string | null;
}

export interface CustomerFlag {
  id: string;
  email: string;
  customer_sub: string | null;
  flag_type: string;
  reason: string;
  order_id: string | null;
  dispute_id: string | null;
  cleared_at: string | null;
  created_at: string;
}

export const disputeEndpoints = {
  list: (openOnly: boolean) => api.get<{ disputes: Dispute[] }>(`/api/admin/disputes${openOnly ? '?open=true' : ''}`),
  get: (id: string) => api.get<{ dispute: Dispute; flags: CustomerFlag[] }>(`/api/admin/disputes/${id}`),
  revokeTickets: (id: string) => api.post<{ revoked: number; dispute: Dispute }>(`/api/admin/disputes/${id}/revoke-tickets`),
  reinstateTickets: (id: string) => api.post<{ restored: number; dispute: Dispute }>(`/api/admin/disputes/${id}/reinstate-tickets`),
  note: (id: string, input: { note?: string | null; evidence_submitted?: boolean }) => api.post<{ dispute: Dispute }>(`/api/admin/disputes/${id}/note`, input),
  flags: (email: string) => api.get<{ flags: CustomerFlag[] }>(`/api/admin/disputes/flags/search?email=${encodeURIComponent(email)}`),
  clearFlag: (flagId: string, reason: string) => api.post<{ cleared: boolean }>(`/api/admin/disputes/flags/${flagId}/clear`, { reason }),
};
